import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth, ApiError } from '../lib/api';

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }

      try {
        const result = await auth.validateInvite(token);
        if (result.valid) {
          setIsValid(true);
          setInviteEmail(result.email);
          setInviteRole(result.role);
        }
      } catch {
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 8) {
      setError('パスワードは8文字以上必要です');
      return;
    }

    setIsLoading(true);

    try {
      await auth.acceptInvite(token!, name, password);
      navigate('/dashboard');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('登録に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: '管理者',
      user: 'ユーザー',
      viewer: '閲覧者',
    };
    return labels[role] || role;
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-jarvis-navy-dark via-jarvis-navy to-jarvis-navy-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-jarvis-gold border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">招待リンクを確認中...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-jarvis-navy-dark via-jarvis-navy to-jarvis-navy-dark flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              招待リンクが無効です
            </h2>
            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              この招待リンクは期限切れか、既に使用されています。
              管理者に新しい招待を依頼してください。
            </p>
            <Link to="/login" className="btn-primary inline-block">
              ログインページへ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-jarvis-navy-dark via-jarvis-navy to-jarvis-navy-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* ロゴ */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-jarvis-gold to-jarvis-gold-dark flex items-center justify-center">
            <span className="text-jarvis-navy-dark font-bold text-4xl">J</span>
          </div>
          <h1 className="text-white text-2xl font-bold">Jarvisへようこそ</h1>
          <p className="text-gray-400 text-sm">アカウントを作成してください</p>
        </div>

        {/* フォーム */}
        <div className="card">
          <div className="mb-6 p-3 rounded-lg bg-jarvis-gold/10 border border-jarvis-gold/30">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              <strong>{inviteEmail}</strong> として招待されました
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              ロール: {getRoleLabel(inviteRole)}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                お名前 *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="山田 太郎"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                パスワード *
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="8文字以上"
                required
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                パスワード（確認） *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="もう一度入力"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? '登録中...' : 'アカウントを作成'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

